import {
  assetDetailSchema,
  assetListResponseSchema,
  assetLookupsSchema,
  assetOkSchema,
  type AssetListQuery,
  type CreateAssetRequest,
  type LinkAssetTicketRequest,
  type UpdateAssetRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

export const assetsQueryKey = ['assets'] as const;
export const assetLookupsQueryKey = ['assets', 'lookups'] as const;

export function assetQueryKey(id: string) {
  return ['assets', id] as const;
}

export function useAssetLookups() {
  return useQuery({
    queryKey: assetLookupsQueryKey,
    queryFn: () => apiFetch('/assets/lookups', assetLookupsSchema),
  });
}

export function useAssets(query: AssetListQuery, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...assetsQueryKey, query],
    enabled: options?.enabled ?? true,
    queryFn: () =>
      apiFetch(
        `/assets${toSearchParams({
          page: query.page,
          pageSize: query.pageSize,
          sortBy: query.sortBy,
          sortOrder: query.sortOrder,
          q: query.q,
          status: query.status,
          assetTypeId: query.assetTypeId,
          departmentId: query.departmentId,
          assignedUserId: query.assignedUserId,
          ticketId: query.ticketId,
        })}`,
        assetListResponseSchema,
      ),
  });
}

export function useAsset(id: string | undefined) {
  return useQuery({
    queryKey: id ? assetQueryKey(id) : ['assets', 'missing'],
    queryFn: () => apiFetch(`/assets/${id}`, assetDetailSchema),
    enabled: Boolean(id),
  });
}

async function invalidateAssets(queryClient: ReturnType<typeof useQueryClient>, id?: string) {
  await queryClient.invalidateQueries({ queryKey: assetsQueryKey });
  if (id) {
    await queryClient.invalidateQueries({ queryKey: assetQueryKey(id) });
  }
}

export function useCreateAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAssetRequest) =>
      apiFetch('/assets', assetDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (asset) => {
      await invalidateAssets(queryClient, asset.id);
    },
  });
}

export function useUpdateAsset(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateAssetRequest) =>
      apiFetch(`/assets/${id}`, assetDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async (asset) => {
      queryClient.setQueryData(assetQueryKey(id), asset);
      await invalidateAssets(queryClient, asset.id);
    },
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/assets/${id}`, assetOkSchema, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await invalidateAssets(queryClient);
    },
  });
}

export function useLinkAssetTicket(assetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LinkAssetTicketRequest) =>
      apiFetch(`/assets/${assetId}/tickets`, assetDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (asset) => {
      queryClient.setQueryData(assetQueryKey(assetId), asset);
      await invalidateAssets(queryClient, asset.id);
    },
  });
}

export function useUnlinkAssetTicket(assetId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ticketId: string) =>
      apiFetch(`/assets/${assetId}/tickets/${ticketId}`, assetDetailSchema, {
        method: 'DELETE',
      }),
    onSuccess: async (asset) => {
      queryClient.setQueryData(assetQueryKey(assetId), asset);
      await invalidateAssets(queryClient, asset.id);
    },
  });
}

export function useAttachTicketAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, body }: { assetId: string; body: LinkAssetTicketRequest }) =>
      apiFetch(`/assets/${assetId}/tickets`, assetDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (asset) => {
      await invalidateAssets(queryClient, asset.id);
    },
  });
}

export function useDetachTicketAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, ticketId }: { assetId: string; ticketId: string }) =>
      apiFetch(`/assets/${assetId}/tickets/${ticketId}`, assetDetailSchema, {
        method: 'DELETE',
      }),
    onSuccess: async (asset) => {
      await invalidateAssets(queryClient, asset.id);
    },
  });
}
